import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import apiCoreAdapter from "../../hooks/apiCoreAdapter";
import type { PatientDetailData } from "../../hooks/patients/usePatientDetail";

export type EmergencyContact = {
  id: string;
  name: string;
  phone: string;
  relationship: string;
};

/** Solapa "Información": motivo de consulta, datos del paciente, contactos de emergencia y la nota personal (privada, no es historia clínica). */
export function InfoTab({
  processId,
  patient,
  contacts,
  readOnly,
}: {
  processId: string;
  patient: PatientDetailData | null;
  contacts: EmergencyContact[];
  readOnly: boolean;
}) {
  const [note, setNote] = useState("");
  const [baseline, setBaseline] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiCoreAdapter
      .getPatientPersonalNote(processId)
      .then((r) => {
        setNote(r.content ?? "");
        setBaseline(r.content ?? "");
      })
      .catch(() => undefined);
  }, [processId]);

  const save = async () => {
    setSaving(true);
    setError(false);
    try {
      const r = await apiCoreAdapter.updatePatientPersonalNote(processId, note);
      setBaseline(r.content ?? note);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const row = (label: string, value?: string | null) =>
    value ? (
      <p>
        <span className="text-gray-500">{label}: </span>
        {value}
      </p>
    ) : null;

  return (
    <div className="space-y-4 text-sm">
      <section>
        <h3 className="mb-1 font-semibold">Motivo de consulta</h3>
        <p className="whitespace-pre-wrap">
          {patient?.reason || "Sin motivo de consulta cargado."}
        </p>
      </section>
      <section className="space-y-0.5">
        <h3 className="mb-1 font-semibold">Datos del paciente</h3>
        {row("Nombre", patient?.fullname)}
        {row("Fecha de nacimiento", patient?.profile?.birthDate)}
        {row("País", patient?.profile?.country)}
        {row("Huso horario", patient?.profile?.timezone)}
        {row("Teléfono", patient?.phone)}
      </section>
      <section>
        <h3 className="mb-1 font-semibold">Contactos de emergencia</h3>
        {contacts.length === 0 && (
          <p className="text-gray-500">
            No hay contactos de emergencia cargados.
          </p>
        )}
        <ul className="space-y-1">
          {contacts.map((c) => (
            <li key={c.id}>
              {c.name} ({c.relationship}) · {c.phone}
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h3 className="mb-1 font-semibold">Notas personales</h3>
        <textarea
          className="h-24 w-full rounded-lg border p-2"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={readOnly}
          aria-label="Notas personales"
          placeholder="Recordatorio privado: no forma parte de la historia clínica."
        />
        <div className="mt-1 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={readOnly || saving || note === baseline}
            onClick={() => void save()}
          >
            {saving ? "Guardando…" : "Guardar nota"}
          </Button>
          {error && (
            <span className="text-xs text-red-600">No pudimos guardar.</span>
          )}
        </div>
      </section>
    </div>
  );
}
